import './index.scss';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeRoles } from '@actions/roleActions';
import { Pagination } from '@components/Pages/AnswersPage/constants';
import { UnitSearch } from '@components/Pages/DashboardPage/PermissionManagementPage/UnitsSearch';
import {
  makeAddUnitRole,
  makeRemoveUnitRole,
  makeUnitsWithRoles,
  setUnitsFiltersPartially,
} from '@actions/unitActions';
import {
  canUpdateUnitRoleSelector,
  unitsFiltersSelector,
  unitsRowsPerPageSelector,
  unitsSelector,
} from '@selectors/unit';
import { UnitsPermissionsPagination } from '@components/Pages/DashboardPage/PermissionManagementPage/UnitsPermissionsPagination';
import { UnitEntityInterface } from '@types';
import PermissionsSelect from '../PermissionsSelect';

export const UnitsPermissionsTab = () => {
  const dispatch = useDispatch();
  // const [selectedUnits, setSelectedUnits] = useState([]); //TODO: server pagination
  const [tableUnits, setTableUnits] = useState<UnitEntityInterface[]>([]);
  const unitsWithPermissions = useSelector(unitsSelector);
  const rowsPerPage = useSelector(unitsRowsPerPageSelector);
  const canUpdateUnitRole = useSelector(canUpdateUnitRoleSelector);

  const filters = useSelector(unitsFiltersSelector);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(tableUnits.length);

  const lastElementIndex = rowsPerPage * (page + 1);
  const pages = useMemo(() => Math.ceil(total / rowsPerPage), [total, rowsPerPage]);

  const unitsWithPermissionsPage = tableUnits.slice(
    lastElementIndex - rowsPerPage,
    lastElementIndex
  );

  useEffect(() => setTotal(tableUnits.length), [tableUnits]);

  useEffect(() => {
    setTableUnits((prevTableUnits) => {
      const changedUnits = prevTableUnits.reduce(
        (acc, tableUnit) => [
          ...acc,
          unitsWithPermissions.find(
            (unitsWithPermission) => unitsWithPermission.id === tableUnit.id
          ) ?? { ...tableUnit, roles: [] },
        ],
        []
      );
      const currentUnits = unitsWithPermissions.filter(
        (unitsWithPermission) => !changedUnits.find(({ id }) => unitsWithPermission.id === id)
      );

      return [...changedUnits, ...currentUnits];
    });
  }, [unitsWithPermissions]);

  useEffect(() => {
    dispatch(makeRoles());
    dispatch(makeUnitsWithRoles({ ...filters, ...{ offset: 0, limit: 10000, withRoles: true } }));
  }, []);

  const handleChangePage = (event: ChangeEvent<HTMLInputElement>, newPage: number): void => {
    setPage(newPage - 1);
  };

  const handleChangePageNumber = (value: number): void => {
    if (value) {
      setPage(value - 1);
    }
  };

  const handleChangeRowsPerPage = (value: number) => {
    const unitsOnPageCount = value || Pagination.UNITS_ON_ADMIN_PAGE_COUNT_FIRST;
    dispatch(setUnitsFiltersPartially({ offset: 0, limit: unitsOnPageCount }));
    setPage(0);
  };

  const handleChangePermission = (unitId: string, roleId: string, isUserRole: boolean) => {
    if (isUserRole) {
      dispatch(makeRemoveUnitRole(unitId, { updateChildren: false }));
    } else {
      dispatch(makeAddUnitRole(unitId, { roleId, updateChildren: false }));
      // setSelectedUnits((prev) => [...prev.filter((unit) => unit.id !== unitId)]); //TODO: server pagination
    }
  };

  const onSearch = useCallback(
    (_event, { value }) => {
      setTableUnits((prev) => [value, ...prev]);
      dispatch(setUnitsFiltersPartially({ offset: 0 }));
      setPage(0);
      // setSelectedUnits((prev) => [...prev, value]); //TODO: server pagination
    },
    [unitsWithPermissions]
  );

  return (
    <div className={'units-permissions-tab'}>
      <div>
        <div className={'units-permissions-tab__search-wrapper'}>
          <UnitSearch onChange={onSearch} tableUnits={tableUnits} />
        </div>
        <div className={'units-permissions-tab__permissions-wrapper'}>
          {unitsWithPermissionsPage.map(({ id, roles, longName }) => {
            return (
              <PermissionsSelect
                key={id}
                id={id}
                label={longName}
                canUpdateRole={canUpdateUnitRole}
                onCheckPermission={handleChangePermission}
                rowRoles={roles}
              />
            );
          })}
        </div>
      </div>
      <UnitsPermissionsPagination
        page={page}
        total={total}
        pages={pages}
        handleChangePage={handleChangePage}
        handleChangePageNumber={handleChangePageNumber}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </div>
  );
};
